import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Users, ClipboardPaste, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const since = (iso) => {
  if (!iso) return "Never contacted";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "Contacted today";
  if (days === 1) return "Contacted yesterday";
  return `Contacted ${days} days ago`;
};

export const ContactListCard = ({ authHeader, onChanged }) => {
  const [contacts, setContacts] = useState(null);
  const [kinds, setKinds] = useState({});
  const [name, setName] = useState("");
  const [kind, setKind] = useState("past_client");
  const [paste, setPaste] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bdm/contacts`, { headers: authHeader() });
      setContacts(data.contacts);
      setKinds(data.kinds);
    } catch {
      setContacts([]);
    }
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/bdm/contacts`, { name, kind, note: "" }, { headers: authHeader() });
      setName("");
      await load();
      onChanged?.();
      toast.success("Added — they'll show up in your plan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that name.");
    } finally {
      setBusy(false);
    }
  };

  const addMany = async () => {
    if (!paste.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/bdm/contacts/bulk`,
        { names: paste, kind },
        { headers: authHeader() },
      );
      setPaste("");
      setShowPaste(false);
      await load();
      onChanged?.();
      toast.success(`${data.added} name${data.added === 1 ? "" : "s"} added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read that list.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/bdm/contacts/${id}`, { headers: authHeader() });
    await load();
    onChanged?.();
  };

  if (!contacts) {
    return (
      <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-6 flex items-center gap-2 text-sm text-ink-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your list…
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-6" data-testid="contact-list-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal font-bold">
        <Users size={13} /> My list
      </div>
      <h3 className="mt-1 font-heading font-medium text-xl text-navy">
        {contacts.length === 0 ? "Who do you already talk to?" : `${contacts.length} on your list`}
      </h3>
      <p className="mt-1 text-sm text-ink-500">
        Just first names or full names — this is what lets your daily move name a real person instead
        of saying "contact someone".
      </p>

      <div className="mt-4 flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="e.g. Sarah Chen"
          data-testid="contact-name-input"
          className="flex-1 rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
        />
        <button
          onClick={add}
          disabled={busy || !name.trim()}
          data-testid="contact-add-button"
          className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white font-semibold text-sm px-4 py-2.5 rounded-full transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          data-testid="contact-kind-select"
          className="rounded-full border border-linen-border bg-white px-3 py-1.5 text-xs text-navy focus:outline-none focus:ring-2 focus:ring-teal/40"
        >
          {Object.entries(kinds).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <button
          onClick={() => setShowPaste((v) => !v)}
          data-testid="contact-paste-toggle"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500 hover:text-teal transition-colors"
        >
          <ClipboardPaste size={13} /> Paste a whole list
        </button>
      </div>

      {showPaste && (
        <div className="mt-3">
          <textarea
            rows={4}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder={"One name per line, or separated by commas"}
            data-testid="contact-paste-input"
            className="w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
          />
          <button
            onClick={addMany}
            disabled={busy || !paste.trim()}
            data-testid="contact-paste-save"
            className="mt-2 inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white font-semibold text-sm px-4 py-2.5 rounded-full transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add them all
          </button>
        </div>
      )}

      {contacts.length > 0 && (
        <ul className="mt-5 space-y-1.5 max-h-80 overflow-y-auto" data-testid="contact-list">
          {contacts.map((c) => (
            <li
              key={c.id}
              data-testid={`contact-${c.id}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-linen-border px-3.5 py-2.5"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-navy truncate">{c.name}</p>
                <p className="text-[11px] text-ink-400">
                  {kinds[c.kind] || "Other"} · {since(c.last_touched_at)}
                </p>
              </div>
              <button
                onClick={() => remove(c.id)}
                data-testid={`contact-remove-${c.id}`}
                aria-label={`Remove ${c.name}`}
                className="shrink-0 text-ink-300 hover:text-red-600 transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-4 flex items-start gap-1.5 text-[11px] text-ink-400 leading-relaxed">
        <ShieldCheck size={13} className="shrink-0 mt-0.5" />
        Names only — we never ask for phone numbers or emails, and nothing here is shared with your
        loan officer or anyone else.
      </p>
    </div>
  );
};

export default ContactListCard;
